import { QuestionTypes } from '@showbie-socrative/socrative-utils';

import { EventAdapter } from '~analytics/adapters/EventAdapter';
import { sendTeacherAmplitudeEvent } from '~analytics/amplitude';
import {
  QuestionTypes as AnalyticsQuestionTypes,
  EventTypes,
} from '~analytics/types';
import { mapQuestionType } from '~analytics/utils/mapQuestionType';

import {
  questionTypeChangeCancelledEventMetadata,
  questionTypeChangeConfirmedEventMetadata,
  questionTypeChangeInitiatedEventMetadata,
} from './QuestionTypeChangeEventAdapter.metadata';

// ─── Initiated ───────────────────────────────────────────────────────────────

interface QuestionTypeChangeInitiatedParams {
  fromType: QuestionTypes | string;
  quizId: number;
  questionPosition: number;
}

type QuestionTypeChangeInitiatedData = {
  from_type: AnalyticsQuestionTypes | null;
  quiz_id: number;
  question_position: number;
};

export class QuestionTypeChangeInitiatedEventAdapter extends EventAdapter<QuestionTypeChangeInitiatedData> {
  protected intercomEventKey = null;
  static metadata = questionTypeChangeInitiatedEventMetadata;

  constructor({
    fromType,
    quizId,
    questionPosition,
  }: QuestionTypeChangeInitiatedParams) {
    super();
    this.eventData = {
      from_type: mapQuestionType(fromType),
      quiz_id: quizId,
      question_position: questionPosition,
    };
  }

  protected amplitudeEventFunction =
    function sendQuestionTypeChangeInitiatedEvent(
      event: QuestionTypeChangeInitiatedData
    ) {
      sendTeacherAmplitudeEvent(EventTypes.QuestionTypeChangeInitiated, event);
    };
}

// ─── Confirmed ───────────────────────────────────────────────────────────────

interface QuestionTypeChangeConfirmedParams {
  fromType: QuestionTypes | string;
  toType: QuestionTypes | string;
  quizId: number;
  questionPosition: number;
}

type QuestionTypeChangeConfirmedData = {
  from_type: AnalyticsQuestionTypes | null;
  to_type: AnalyticsQuestionTypes | null;
  quiz_id: number;
  question_position: number;
};

export class QuestionTypeChangeConfirmedEventAdapter extends EventAdapter<QuestionTypeChangeConfirmedData> {
  protected intercomEventKey = null;
  static metadata = questionTypeChangeConfirmedEventMetadata;

  constructor({
    fromType,
    toType,
    quizId,
    questionPosition,
  }: QuestionTypeChangeConfirmedParams) {
    super();
    this.eventData = {
      from_type: mapQuestionType(fromType),
      to_type: mapQuestionType(toType),
      quiz_id: quizId,
      question_position: questionPosition,
    };
  }

  protected amplitudeEventFunction =
    function sendQuestionTypeChangeConfirmedEvent(
      event: QuestionTypeChangeConfirmedData
    ) {
      sendTeacherAmplitudeEvent(EventTypes.QuestionTypeChangeConfirmed, event);
    };
}

// ─── Cancelled ───────────────────────────────────────────────────────────────

interface QuestionTypeChangeCancelledParams {
  fromType: QuestionTypes | string;
  attemptedToType: QuestionTypes | string;
  quizId: number;
}

type QuestionTypeChangeCancelledData = {
  from_type: AnalyticsQuestionTypes | null;
  attempted_to_type: AnalyticsQuestionTypes | null;
  quiz_id: number;
};

export class QuestionTypeChangeCancelledEventAdapter extends EventAdapter<QuestionTypeChangeCancelledData> {
  protected intercomEventKey = null;
  static metadata = questionTypeChangeCancelledEventMetadata;

  constructor({
    fromType,
    attemptedToType,
    quizId,
  }: QuestionTypeChangeCancelledParams) {
    super();
    this.eventData = {
      from_type: mapQuestionType(fromType),
      attempted_to_type: mapQuestionType(attemptedToType),
      quiz_id: quizId,
    };
  }

  protected amplitudeEventFunction =
    function sendQuestionTypeChangeCancelledEvent(
      event: QuestionTypeChangeCancelledData
    ) {
      sendTeacherAmplitudeEvent(EventTypes.QuestionTypeChangeCancelled, event);
    };
}
